import React from 'react';
import '../styles/brand.css';

const COLORS = [
  { name: 'Deep', hex: '#10303D', use: 'Primary surface — hero, nav, contact section' },
  { name: 'Teal', hex: '#00D4B4', use: 'Accent — CTAs, dots, top rules, active states', light: false },
  { name: 'Teal Ink', hex: '#0A8F7B', use: 'Accent text on light bg — numbers, sub-labels' },
  { name: 'Ink', hex: '#1C2B33', use: 'Primary body text' },
  { name: 'Mute', hex: '#5F6F78', use: 'Secondary text — captions, ledes, meta' },
  { name: 'Line', hex: '#D8E1E4', use: 'Hairlines, form borders, dividers' },
  { name: 'Mist', hex: '#EEF6F4', use: 'Subtle surface — experience band, notes' },
  { name: 'White', hex: '#FFFFFF', use: 'Base surface — sections, cards' },
];

const VOICE = [
  {
    title: 'Concrete over abstract',
    body: 'Numbers, dates, dollar figures. Never generic buzzwords.',
    example: '"$2.87M ARR protected once leadership could see it."',
  },
  {
    title: 'Plain over polished',
    body: 'Short sentences. Everyday words. Read like a human, not a résumé.',
    example: '"Support that costs less every quarter and tells you what to build next."',
  },
  {
    title: 'Show the mechanism',
    body: 'Explain how the outcome happened, not just what it was.',
    example: '"Feed the taxonomy into the roadmap. Top categories down 40%."',
  },
  {
    title: 'Confident, never boastful',
    body: 'State the result, then move on. Let the numbers do the work.',
    example: '"5x Stevie Award winner. Customer Support Team of the Year."',
  },
];

const Brand = () => {
  const handlePrint = () => window.print();

  return (
    <div className="brand-board">
      <div className="wrap">
        <header className="top">
          <div>
            <div className="kicker">Brand Board</div>
            <h1 className="serif">Phillip Labossiere</h1>
            <p>Design system, tone of voice, and visual language for philliplabossiere.me. Save this page as a PDF for future collaborators.</p>
          </div>
          <button className="savebtn" onClick={handlePrint} type="button" data-testid="brand-save-pdf">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"/></svg>
            Save as PDF
          </button>
        </header>

        {/* COLORS */}
        <section className="sec">
          <h2>Colors</h2>
          <p className="sub">Deep-teal grounds every dark surface. Teal is the single accent — use it sparingly for emphasis.</p>
          <div className="swatches">
            {COLORS.map(c => (
              <div className="sw" key={c.hex}>
                <div className="chip" style={{ background: c.hex, borderBottom: c.hex === '#FFFFFF' ? '1px solid #D8E1E4' : 'none' }} />
                <div className="meta">
                  <div className="name">{c.name}</div>
                  <div className="hex">{c.hex}</div>
                  <div className="use">{c.use}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* TYPOGRAPHY */}
        <section className="sec">
          <h2>Typography</h2>
          <p className="sub">Two families, one from Google Fonts. Serif carries display and quiet authority; sans carries the workhorse text.</p>

          <div className="fontcard">
            <div className="fserif">
              <div className="fname">Display</div>
              <div className="fsample">IBM Plex Serif</div>
              <div className="fuse">Weights 400 · 500 (italic 400). Used for h1/h2/h4, stat numerals, and operating-model step titles.</div>
            </div>
            <div className="fsans">
              <div className="fname">Body & UI</div>
              <div className="fsample">IBM Plex Sans</div>
              <div className="fuse">Weights 400 · 500 · 600. Body copy, buttons, labels, nav, all form elements.</div>
            </div>
          </div>

          <div className="type-row">
            <div className="lbl">H1 — Display</div>
            <div className="sample h1 serif">Support that costs less every quarter</div>
          </div>
          <div className="type-row">
            <div className="lbl">H2 — Section</div>
            <div className="sample h2 serif">The operating model I bring to every team</div>
          </div>
          <div className="type-row">
            <div className="lbl">Lede</div>
            <div className="sample hero-p">15+ years running CX and operations at PE- and VC-backed SaaS companies.</div>
          </div>
          <div className="type-row">
            <div className="lbl">Body</div>
            <div className="sample body">Tiered ticket taxonomy built with Engineering and Product, so every contact becomes a data point about what's broken.</div>
          </div>
          <div className="type-row">
            <div className="lbl">Kicker · Meta</div>
            <div className="sample small" style={{ textTransform: 'uppercase' }}>June 2025 – present</div>
          </div>
        </section>

        {/* LOGO & WORDMARK */}
        <section className="sec">
          <h2>Logo &amp; Wordmark</h2>
          <p className="sub">"PJL" monogram on deep-teal to teal-ink gradient. Wordmark uses IBM Plex Serif medium; role tagline in teal.</p>

          <div className="logo-grid">
            <div className="logo-tile">
              <span className="lbl">Monogram · light</span>
              <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center' }}>
                <div className="pjl sm">PJL</div>
                <div className="pjl">PJL</div>
                <div className="pjl lg">PJL</div>
              </div>
            </div>
            <div className="logo-tile mist">
              <span className="lbl">Wordmark · light</span>
              <div className="wordmark">
                <span className="name serif">Phillip Labossiere</span>
                <span className="role">Customer Support, CX &amp; Operations Leader</span>
              </div>
            </div>
            <div className="logo-tile dark">
              <span className="lbl">Wordmark · dark</span>
              <div className="wordmark">
                <span className="name serif" style={{ color: '#fff' }}>Phillip Labossiere</span>
                <span className="role">Customer Support, CX &amp; Operations Leader</span>
              </div>
            </div>
          </div>
        </section>

        {/* UI ELEMENTS */}
        <section className="sec">
          <h2>UI Elements</h2>
          <p className="sub">The building blocks — always drawn from CSS variables in <code>revamp.css</code>. Don't invent new radii or borders.</p>

          <div className="ui-grid">
            <div className="ui-tile">
              <h4>Buttons</h4>
              <div className="row">
                <span className="btn">Get in touch</span>
                <span className="btn line">LinkedIn</span>
                <span className="btn ink">Request clerking</span>
              </div>
            </div>

            <div className="ui-tile">
              <h4>Chips &amp; Badges</h4>
              <div className="row">
                <span className="chip">Text OK</span>
                <span className="chip">Fine art</span>
                <span className="chip">Verified</span>
              </div>
            </div>

            <div className="ui-tile">
              <h4>Card</h4>
              <div className="card-preview">
                <h5 className="serif">Auction clerking</h5>
                <p>Clerking for live, online, and hybrid sales.</p>
              </div>
            </div>

            <div className="ui-tile">
              <h4>Top-rule accent</h4>
              <div className="top-rule">
                5x Stevie Award winner
                <small>Customer Support Team of the Year</small>
              </div>
            </div>

            <div className="ui-tile">
              <h4>Form field</h4>
              <span className="field">plabossiere@gmail.com</span>
            </div>

            <div className="ui-tile">
              <h4>Stat numeral</h4>
              <div className="serif" style={{ fontSize: '2.4rem', lineHeight: 1, letterSpacing: '-.02em' }}>$2.87M</div>
              <div style={{ color: 'var(--mute)', fontSize: '.85rem', marginTop: '.3rem' }}>revenue impact</div>
            </div>
          </div>
        </section>

        {/* VOICE & TONE */}
        <section className="sec">
          <h2>Voice &amp; Tone</h2>
          <p className="sub">Four principles. If a sentence violates any of them, rewrite it.</p>

          <div className="voice-grid">
            {VOICE.map((v, i) => (
              <div className="voice" key={i}>
                <h4>{v.title}</h4>
                <p>{v.body}</p>
                <blockquote>{v.example}</blockquote>
              </div>
            ))}
          </div>
        </section>

        <footer className="bb-foot">
          <span>© {new Date().getFullYear()} Phillip Labossiere</span>
          <span>Brand Board · philliplabossiere.me/brand</span>
        </footer>
      </div>
    </div>
  );
};

export default Brand;
